import { createSelector } from '@reduxjs/toolkit';
import { get, getOr, isEmpty, sortBy, uniqWith, isNil, uniqBy, identity } from 'lodash/fp';
import { toStringFqn, fqnRegExp } from 'app/lib/utils';
import { TypeFamily } from 'app/lib/types/server';
import { selectEntitiesById, selectEntityId } from 'app/store/entities/selectors.js';
import { selectCurrentViewId, selectSchemaForCurrentView } from 'app/store/view/selectors.js';
import { QueryState, QUERY_HISTORY_FQN_STRING, EVENTS_TABLE_FQN_STRING } from 'app/lib/constants';
import { isDate, isNumeric } from 'app/lib/utils/sql';
import { selectWorkbenchDatasetTabForEntityId } from 'app/store/preferences/selectors';
import { selectCurrentFqn, selectColumnSchema } from 'app/store/findDatasets/selectors.js';
import {
    selectExploreQueries,
    selectQueryMetadataForPropKey,
    selectQueryResultsForPropKey,
    selectQueryResultsForPropQueryUuid,
    selectQueryResultsForPropEntityId,
    selectQueryMetadataForPropEntityId
} from 'app/store/snowflakeQueries/selectors.js';
import { QueryKeys } from 'app/store/snowflakeQueries';
import { isDatasetLoaded } from 'app/lib/utils/isDatasetLoaded.js';
import { isDatasetsList, isUsageReportingPage } from 'app/routes/routes.js';
import {
    distinct,
    from,
    identifier,
    limit,
    select,
    sql,
    where
} from 'app/lib/utils/sql_generator.js';
import { selectQueryHistorySchema } from 'app/store/usageReporting/selectors.js';
import { selectEventsTableSchema } from 'app/store/ROIReporting/selectors.js';
import { selectOperationInputSchema } from 'app/store/operations/selectors.js';
import { filters, havingClause } from './utils.js';

const EXPLORE_QUERY_COUNT_COLUMN = 'COUNT(0) OVER ()';

const selectSlice = get('exploreData');
const selectPropQueryUuid = (_, props) => props.queryUuid;
const selectPropEntityId = (_, props) => props.entityId;
const selectPropFqn = (_, props) => props.fqn;

export const selectQueriesByEntityId = createSelector(selectSlice, get('byEntityId'));

export const selectNextAvailableIndexForEntityId = createSelector(
    selectPropFqn,
    selectCurrentViewId,
    selectPropEntityId,
    selectQueriesByEntityId,
    (propFqn, currentEntityId, propEntityId, queriesByEntityId) => {
        const entityId = toStringFqn(propFqn) ?? propEntityId ?? currentEntityId;
        const queries = queriesByEntityId[entityId];

        if (isEmpty(queries)) {
            return 1;
        }

        const last = Object.values(queries).at(-1);
        return last ? last.model.index + 1 : 1;
    }
);

const selectQueriesByEntityPropId = createSelector(
    selectEntityId,
    selectCurrentFqn,
    selectQueriesByEntityId,
    selectPropFqn,
    selectCurrentViewId,
    (entityId, currentFqn, queries, propFqn, currentViewId) => {
        if (isUsageReportingPage()) {
            return queries?.[toStringFqn(propFqn)];
        }
        if (isDatasetsList()) {
            return queries?.[toStringFqn(currentFqn)];
        }
        const id = toStringFqn(propFqn) ?? entityId ?? currentViewId;
        return queries?.[id];
    }
);

export const selectQueryForPropEntityIdQueryUuid = createSelector(
    selectPropEntityId,
    selectPropQueryUuid,
    selectQueriesByEntityId,
    (entityId, queryUuid, queries) => queries[entityId][queryUuid]
);

export const selectQueryListForPropEntityId = createSelector(
    selectPropEntityId,
    selectPropFqn,
    selectQueriesByEntityId,
    selectExploreQueries,
    (entityId, propFqn, queriesByEntityId, snowflakeQueries) => {
        const maybeQueries = queriesByEntityId[entityId ?? toStringFqn(propFqn)];
        if (maybeQueries === undefined) {
            return null;
        }

        return sortBy(
            'model.index',
            Object.values(maybeQueries).map((query) => ({
                ...query,
                state: snowflakeQueries[query.model.queryUuid]?.state
            }))
        );
    }
);

export const selectQueryHistoryQueries = createSelector(identity, (state) =>
    selectQueryListForPropEntityId(state, { entityId: QUERY_HISTORY_FQN_STRING })
);

export const selectAssetRoiQueries = createSelector(identity, (state) =>
    selectQueryListForPropEntityId(state, { entityId: EVENTS_TABLE_FQN_STRING })
);

const selectCurrentQueryUuid = createSelector(selectSlice, get('currentQueryUuid'));

const selectQueryUuid = createSelector(
    selectCurrentQueryUuid,
    selectPropQueryUuid,
    selectWorkbenchDatasetTabForEntityId,
    (currentQueryUuid, propQueryUuid, tabQueryUuid) =>
        currentQueryUuid || propQueryUuid || tabQueryUuid
);

export const selectQueryByUuid = createSelector(selectQueryUuid, selectQueriesByEntityPropId, get);

export const selectQueryForPropEntityIdAndQueryUuid = createSelector(
    selectQueriesByEntityId,
    selectPropEntityId,
    selectPropQueryUuid,
    (queriesByEntityId, entityId, queryUuid) => queriesByEntityId[entityId]?.[queryUuid]
);

const selectCurrentEntityId = createSelector(selectSlice, get('currentEntityId'));

const selectQueryByEntityId = createSelector(
    [selectCurrentEntityId, selectEntitiesById],
    (entityId, entities) => {
        const entity = entities[entityId];

        if (isNil(entity)) {
            return null;
        }

        return {
            model: entity.payload.model,
            entityId
        };
    }
);

export const selectQueryByUuidOrEntityId = createSelector(
    [selectQueryByEntityId, selectQueryByUuid],
    (entityQuery, exploreQuery) => entityQuery || exploreQuery
);

const selectDimensionsByUuid = createSelector(selectQueryByUuid, getOr(null, 'model.dimensions'));

const selectMeasuresByUuid = createSelector(selectQueryByUuid, getOr(null, 'model.measures'));

export const selectHasSql = createSelector(selectQueryByUuid, (query) => !isEmpty(query?.sql));

export const selectIsExploreOpen = createSelector(selectSlice, get('isExploreOpen'));
export const selectExploreMode = createSelector(selectSlice, get('exploreMode'));
export const selectExploreFqn = createSelector(selectSlice, get('currentFqn'));
export const selectIsSaving = createSelector(selectSlice, get('isSaving'));

/** @deprecated Use selectIsExploreOpen */
export const selectIsDialogOpen = selectIsExploreOpen;
/** @deprecated Use selectExploreMode */
export const selectDialogMode = selectExploreMode;

const selectSnowflakeQuery = createSelector(selectQueryUuid, selectExploreQueries, get);

export const selectIsProcessing = createSelector(
    selectSlice,
    selectSnowflakeQuery,
    (slice, snowflakeQuery) => slice.isProcessing || snowflakeQuery?.state === QueryState.RUNNING
);

export const selectError = createSelector(
    selectSlice,
    selectSnowflakeQuery,
    (slice, query) => slice.error || query?.error
);

const selectSchemaForWorkbenchView = createSelector(
    selectOperationInputSchema,
    selectSchemaForCurrentView,
    (inputSchema, currentSchema) => inputSchema ?? currentSchema
);

const selectUsageSchema = createSelector(
    selectQueryHistorySchema,
    selectEventsTableSchema,
    selectPropFqn,
    (historySchema, eventsTableSchema, fqn) =>
        toStringFqn(fqn) === QUERY_HISTORY_FQN_STRING ? historySchema : eventsTableSchema
);

export const selectSchemaForQuery = createSelector(
    selectSchemaForWorkbenchView,
    selectColumnSchema,
    selectUsageSchema,
    selectPropFqn,
    (schema, columnSchema, usageSchema, propFqn) => {
        if (isUsageReportingPage()) {
            return usageSchema;
        }
        if (propFqn) {
            return columnSchema;
        }

        return schema;
    }
);

export const selectOriginalSchemaForQueryUuid = createSelector(selectSnowflakeQuery, (query) => {
    if (query?.data?.columns) {
        const countColumnIndex = query.data.columns.findIndex(
            ({ name }) => name === EXPLORE_QUERY_COUNT_COLUMN
        );

        return countColumnIndex === -1
            ? query.data.columns
            : query.data.columns.toSpliced(countColumnIndex, 1);
    }

    return null;
});

const cmp = (type) =>
    isDate(type) ? (a, b) => a?.toMillis() === b?.toMillis() : (a, b) => a === b;
const LOW_CARDINALITY_THRESHOLD = 100;

export const selectVizOptions = createSelector(
    selectQueryResultsForPropQueryUuid,
    selectDimensionsByUuid,
    selectMeasuresByUuid,
    (result, dimensions, measures) => {
        if (!result) {
            return [];
        }

        const options = ['table'];
        const numColumns = result.columns.length;

        if (
            result.rowCount === 0 ||
            isEmpty(dimensions) ||
            isEmpty(measures) ||
            numColumns < 2 ||
            numColumns > 3
        ) {
            return options;
        }

        const isLowCardinality = (index) =>
            uniqWith(cmp(result.columnDescriptors[index].typeFamily), result.columns[index])
                .length < LOW_CARDINALITY_THRESHOLD;

        if (
            isLowCardinality(0) &&
            (numColumns === 2 || (dimensions.length === 2 && isLowCardinality(1)))
        ) {
            options.push('bar');
        }

        if (isLowCardinality(0) && (numColumns === 2 || measures.length === 2)) {
            options.push('treemap');
        }

        if (
            (isNumeric(result.columnDescriptors[0].typeFamily) ||
                isDate(result.columnDescriptors[0].typeFamily)) &&
            (numColumns === 2 || (dimensions.length === 2 && isLowCardinality(1)))
        ) {
            options.push('line');
        }

        return options;
    }
);

export const selectCurrentViz = createSelector(selectQueryByUuid, getOr('table', 'currentViz'));
export const selectIsInitiatingDownload = createSelector(selectSlice, get('isInitiatingDownload'));

export const selectCannotDownload = createSelector(
    selectHasSql,
    selectQueryResultsForPropQueryUuid,
    (hasSql, result) => !hasSql || !isNil(result?.error)
);

export const selectSelectedTableExplorations = createSelector(
    selectCurrentFqn,
    selectQueriesByEntityId,
    selectPropFqn,
    (fqn, queriesByEntityId, propFqn) => {
        const fqnString = toStringFqn(fqn);
        return queriesByEntityId[isUsageReportingPage() ? propFqn : fqnString];
    }
);

export const selectHasTableExplorationsForPropFqn = createSelector(
    selectQueriesByEntityId,
    selectPropFqn,
    (queriesByEntityId, fqn) => {
        const fqnString = toStringFqn(fqn);
        return (
            (isDatasetsList() || isUsageReportingPage()) && !isEmpty(queriesByEntityId[fqnString])
        );
    }
);

const FILTERABLE_COLUMN_TYPES = [
    TypeFamily.STRING,
    TypeFamily.DATE,
    TypeFamily.TIMESTAMP,
    TypeFamily.INTEGER,
    TypeFamily.DOUBLE,
    'DECIMAL'
];

export const selectDimensionFilters = createSelector(selectQueryByUuidOrEntityId, (query) => {
    if (!query?.model) {
        return null;
    }
    const table = query.model.table;
    const quotedTable = fqnRegExp.test(table) ? table : identifier(table);

    return (
        query.model.table &&
        query.model.dimensions &&
        uniqBy('column.name', query.model.dimensions)
            .filter((d) => FILTERABLE_COLUMN_TYPES.includes(d.column.type))
            .map((d) => ({
                name: d.column.name,
                type: d.column.type,
                selectedValues: query.model.dimensionFilters?.[d.column.name],
                sql:
                    d.column.type === TypeFamily.STRING
                        ? sql(
                              select(distinct(identifier(d.column.name))),
                              from(quotedTable),
                              where(filters(query.model)),
                              havingClause(query.model),
                              limit(100)
                          )
                        : null
            }))
    );
});

export const selectDimensionFilterValues = createSelector(
    (state, { queryUuid, name }) =>
        selectQueryResultsForPropKey(state, {
            queryKey: `metrics.dimensionFilters.byQueryUuid[${queryUuid}][${name}]`
        }),
    (results) => results?.columns[0]
);

export const selectDimensionFilterError = createSelector(
    (state, { queryUuid, name }) =>
        selectQueryMetadataForPropKey(state, {
            queryKey: `metrics.dimensionFilters.byQueryUuid[${queryUuid}][${name}]`
        }),
    (metadata) => metadata?.error
);

export const selectIsDimensionFilterProcessing = createSelector(
    (state, { queryUuid, name }) =>
        selectQueryMetadataForPropKey(state, {
            queryKey: `metrics.dimensionFilters.byQueryUuid[${queryUuid}][${name}]`
        }),
    (metadata) => metadata?.state === QueryState.RUNNING
);

const selectHasExplicitPreview = createSelector(selectSlice, get('hasExplicitPreview'));

const selectCachedEntityDataset = createSelector(identity, (state) => {
    const entityId = selectCurrentEntityId(state);
    return entityId ? selectQueryResultsForPropEntityId(state, { entityId }) : null;
});

const selectCachedEntityMetadata = createSelector(identity, (state) => {
    const entityId = selectCurrentEntityId(state);
    return entityId ? selectQueryMetadataForPropEntityId(state, { entityId }) : null;
});

export const selectCanUseCachedExplorePreview = createSelector(
    selectHasExplicitPreview,
    selectCurrentEntityId,
    selectCachedEntityDataset,
    selectCachedEntityMetadata,
    (hasExplicitPreview, entityId, dataset, metadata) =>
        !hasExplicitPreview && Boolean(entityId) && isDatasetLoaded(dataset, metadata)
);

const selectExplorePreviewQueryData = createSelector(identity, (state) =>
    selectQueryResultsForPropKey(state, { queryKey: QueryKeys.EXPLORE_PREVIEW })
);

const selectExplorePreviewQueryMetadata = createSelector(identity, (state) =>
    selectQueryMetadataForPropKey(state, { queryKey: QueryKeys.EXPLORE_PREVIEW })
);

export const selectExplorePreviewData = createSelector(
    selectCanUseCachedExplorePreview,
    selectExplorePreviewQueryData,
    selectCachedEntityDataset,
    (canUseCache, queryData, cachedDataset) => (canUseCache ? cachedDataset : queryData)
);

export const selectExplorePreviewMetadata = createSelector(
    selectCanUseCachedExplorePreview,
    selectExplorePreviewQueryMetadata,
    selectCachedEntityMetadata,
    (canUseCache, queryMetadata, cachedMetadata) => (canUseCache ? cachedMetadata : queryMetadata)
);
